import { useState, useCallback } from 'react';
import { AlertType } from '../components/AlertDialog';

interface AlertState {
  open: boolean;
  type: AlertType;
  title?: string;
  message: string;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
}

const useAlert = () => {
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    type: 'info',
    message: '',
  });

  const showAlert = useCallback((
    type: AlertType,
    message: string,
    options?: {
      title?: string;
      onConfirm?: () => void;
      confirmText?: string;
      cancelText?: string;
      showCancel?: boolean;
    }
  ) => {
    setAlertState({
      open: true,
      type,
      message,
      title: options?.title,
      onConfirm: options?.onConfirm,
      confirmText: options?.confirmText,
      cancelText: options?.cancelText,
      showCancel: options?.showCancel,
    });
  }, []);

  const showError = useCallback((message: string, title?: string) => {
    showAlert('error', message, { title });
  }, [showAlert]);

  const showSuccess = useCallback((message: string, title?: string) => {
    showAlert('success', message, { title });
  }, [showAlert]);

  const showWarning = useCallback((message: string, title?: string) => {
    showAlert('warning', message, { title });
  }, [showAlert]);

  const showInfo = useCallback((message: string, title?: string) => {
    showAlert('info', message, { title });
  }, [showAlert]);

  const showConfirm = useCallback((
    message: string,
    onConfirm: () => void,
    options?: {
      title?: string;
      confirmText?: string;
      cancelText?: string;
    }
  ) => {
    showAlert('warning', message, {
      title: options?.title || 'Confirmation',
      onConfirm,
      confirmText: options?.confirmText || 'Confirmer',
      cancelText: options?.cancelText || 'Annuler',
      showCancel: true,
    });
  }, [showAlert]);

  const hideAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, open: false }));
  }, []);

  return {
    alertState,
    showAlert,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    showConfirm,
    hideAlert,
  };
};

export default useAlert;